import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Calendar, BookOpen, Clock, Scissors, Image, Mail, BarChart3, Settings, LogOut, ExternalLink } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const NAV_ITEMS = [
  { key: "dashboard", to: "/admin", icon: LayoutDashboard },
  { key: "calendar", to: "/admin/agenda", icon: Calendar },
  { key: "bookings", to: "/admin/boekingen", icon: BookOpen },
  { key: "availability", to: "/admin/beschikbaarheid", icon: Clock },
  { key: "services", to: "/admin/diensten", icon: Scissors },
  { key: "photos", to: "/admin/fotos", icon: Image },
  { key: "messages", to: "/admin/berichten", icon: Mail },
  { key: "reports", to: "/admin/rapportage", icon: BarChart3 },
  { key: "settings", to: "/admin/instellingen", icon: Settings },
];

export const AdminLayout = () => {
  const { t } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const loadUnread = () => api.get("/admin/contact-messages/unread-count").then(({ data }) => setUnreadCount(data.count)).catch(() => {});
    loadUnread();
    const interval = setInterval(loadUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen flex bg-[#0A0A0A]">
      <aside className="hidden lg:flex flex-col w-64 border-r border-white/10 bg-[#0D0D0D] p-6">
        <h2 className="font-serif text-lg text-white mb-8">
          Elite<span className="text-gold">The</span>Barber <span className="text-white/30 text-xs">Admin</span>
        </h2>
        <nav className="flex-1 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/admin"}
              data-testid={`admin-nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold" : "text-white/60 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon size={18} /> {t(`admin.nav.${item.key}`)}
              {item.key === "messages" && unreadCount > 0 && (
                <span data-testid="admin-nav-messages-unread-badge" className="ml-auto bg-gold text-white text-[10px] font-semibold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 pt-4 border-t border-white/10">
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-back-to-site" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-white/5 hover:text-white">
            <ExternalLink size={18} /> {t("admin.nav.backToSite")}
          </a>
          <button onClick={handleLogout} data-testid="admin-logout-button" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-red-500/10 hover:text-red-400">
            <LogOut size={18} /> {t("admin.nav.logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden flex items-center gap-2 p-3 border-b border-white/10">
          <div className="flex gap-3 overflow-x-auto flex-1">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.key} to={item.to} end={item.to === "/admin"} data-testid={`admin-nav-mobile-${item.key}`} className={({ isActive }) => `relative text-xs px-2 py-1 whitespace-nowrap ${isActive ? "text-gold" : "text-white/50"}`}>
                {t(`admin.nav.${item.key}`)}
                {item.key === "messages" && unreadCount > 0 && (
                  <span data-testid="admin-nav-mobile-messages-unread-badge" className="absolute -top-1 -right-1.5 bg-gold text-white text-[9px] font-semibold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-1 shrink-0 border-l border-white/10 pl-2">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-back-to-site-mobile" title={t("admin.nav.backToSite")} className="p-2 text-white/60 hover:text-white">
              <ExternalLink size={18} />
            </a>
            <button onClick={handleLogout} data-testid="admin-logout-button-mobile" title={t("admin.nav.logout")} className="p-2 text-white/60 hover:text-red-400">
              <LogOut size={18} />
            </button>
          </div>
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
